import { Injectable } from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ContactType } from '../enums/contact.type';

@Injectable({
  providedIn: 'root'
})
export class ContactValidationService {
  // Track original values to determine if validation should be applied
  private originalValues = new Map<string, any>();

  private readonly ALLOWED_KEYS = {
    BACKSPACE: 8,
    TAB: 9,
    ENTER: 13,
    ESCAPE: 27,
    DELETE: 46,
    LEFT_ARROW: 37,
    UP_ARROW: 38,
    RIGHT_ARROW: 39,
    DOWN_ARROW: 40,
    HOME: 35,
    END: 36,
    A: 65,
    C: 67,
    V: 86,
    X: 88,
  };

  private readonly MOBILE_PHONE_PATTERN = /^[0-9+\-()\s]*$/;
  private readonly MOBILE_PHONE_CLEAN_PATTERN = /[^0-9+\-()\s]/g;

  // Getter for phone number placeholder
  get phoneNumberPlaceholder(): string {
    return 'Number (only numbers, +, -, () allowed)';
  }

  // Getter for phone number error message with example
  get phoneNumberErrorMessage(): string {
    return 'Enter a number using digits, spaces, +, -, and ().';
  }

  // Getter for email placeholder
  get emailPlaceholder(): string {
    return 'Number/Email *';
  }

  // Getter for email error message
  get emailErrorMessage(): string {
    return 'This field is required';
  }

  // Getter for number label
  get numberLabel(): string {
    return 'Number';
  }

  // Getter for email label
  get emailLabel(): string {
    return 'Number/Email';
  }

  // Getter for email input type
  get emailInputType(): string {
    return 'email';
  }

  // Getter for text input type
  get textInputType(): string {
    return 'text';
  }

  // Getter for contact label based on type
  getContactLabel(contactType: string): string {
    if (contactType === ContactType.MOBILE_PHONE || contactType === ContactType.PHONE) {
      return 'Number';
    }
    return 'Number/Email';
  }

  // Getter for contact placeholder based on type
  getContactPlaceholder(contactType: string): string {
    if (contactType === ContactType.MOBILE_PHONE || contactType === ContactType.PHONE) {
      return this.phoneNumberPlaceholder;
    }
    return 'Number/Email *';
  }

  // Getter for contact input type based on type
  getContactInputType(contactType: string): string {
    if (contactType === ContactType.EMAIL) {
      return 'email';
    }
    return 'text';
  }

  // Getter for contact error message based on type
  getContactErrorMessage(contactType: string): string {
    if (contactType === ContactType.MOBILE_PHONE || contactType === ContactType.PHONE) {
      return this.phoneNumberErrorMessage;
    }
    return 'This field is required';
  }

  // Method to get contact field properties based on type
  getContactFieldProperties(contactType: string): {
    label: string;
    placeholder: string;
    inputType: string;
    errorMessage: string;
  } {
    const isPhone = contactType === ContactType.MOBILE_PHONE || contactType === ContactType.PHONE;

    return {
      label: isPhone ? this.numberLabel : this.emailLabel,
      placeholder: isPhone ? this.phoneNumberPlaceholder : this.emailPlaceholder,
      inputType: contactType === ContactType.EMAIL ? this.emailInputType : this.textInputType,
      errorMessage: isPhone ? this.phoneNumberErrorMessage : this.emailErrorMessage,
    };
  }

  // Method to get dynamic error message based on validation state
  getDynamicErrorMessage(contact: UntypedFormGroup): string {
    const valueControl = contact.controls.value as UntypedFormControl;
    const contactType = contact.controls.type.value;
    const contactId = contact.controls.id.value;
    const currentValue = valueControl.value;

    // Check if the value has changed from original
    const controlKey = `${contactId || 'new'}_${contactType}`;
    const originalValue = this.originalValues.get(controlKey);
    const hasChanged = originalValue !== currentValue;

    // Only show validation errors if the value has changed or if it's a new contact (no original value)
    if (!hasChanged && originalValue !== undefined) {
      return ''; // No error message if value hasn't changed
    }

    if (valueControl.hasError('required')) {
      return 'This field is required';
    }

    if (contactType === ContactType.EMAIL && valueControl.hasError('email')) {
      return 'Please enter a valid email address';
    }

    if (
      (contactType === ContactType.MOBILE_PHONE || contactType === ContactType.PHONE) &&
      (valueControl.hasError('invalidPhoneNumber') ||
        this.hasInvalidPhoneCharacters(valueControl.value))
    ) {
      return this.phoneNumberErrorMessage;
    }

    return 'This field is invalid';
  }

  // Method to check if phone number contains invalid characters
  public hasInvalidPhoneCharacters(value: string): boolean {
    if (!value) return false;
    return !this.MOBILE_PHONE_PATTERN.test(value);
  }

  // Method to check if current contact has invalid phone characters
  public hasInvalidPhoneCharactersForContact(contact: UntypedFormGroup): boolean {
    const contactType = contact.controls.type.value;
    const value = contact.controls.value.value;
    const contactId = contact.controls.id.value;

    // Check if the value has changed from original
    const controlKey = `${contactId || 'new'}_${contactType}`;
    const originalValue = this.originalValues.get(controlKey);
    const hasChanged = originalValue !== value;

    // Only check for invalid characters if the value has changed or if it's a new contact
    if (!hasChanged && originalValue !== undefined) {
      return false; // Don't show invalid characters error if value hasn't changed
    }

    if (contactType !== ContactType.MOBILE_PHONE && contactType !== ContactType.PHONE) {
      return false;
    }

    return this.hasInvalidPhoneCharacters(value);
  }

  // Clear original values map to ensure clean state
  clearOriginalValues(): void {
    this.originalValues.clear();
  }

  // Store original value for a contact
  storeOriginalValue(contactId: string | number | null, contactType: string, value: any): void {
    const controlKey = `${contactId || 'new'}_${contactType}`;
    this.originalValues.set(controlKey, value);
  }

  // Remove original value for a contact
  removeOriginalValue(contactId: string | number | null, contactType: string): void {
    const controlKey = `${contactId || 'new'}_${contactType}`;
    this.originalValues.delete(controlKey);
  }

  // Get original value for a contact
  getOriginalValue(contactId: string | number | null, contactType: string): any {
    const controlKey = `${contactId || 'new'}_${contactType}`;
    return this.originalValues.get(controlKey);
  }

  // Check if value has changed from original
  hasValueChanged(contactId: string | number | null, contactType: string, currentValue: any): boolean {
    const originalValue = this.getOriginalValue(contactId, contactType);
    return originalValue !== currentValue;
  }

  /**
   * Handles keyboard input validation for contact fields
   * @param event The keyboard event
   * @param contactType The type of contact field
   */
  public onKeyDown(event: KeyboardEvent, contactType: ContactType): void {
    if (contactType !== ContactType.MOBILE_PHONE && contactType !== ContactType.PHONE) {
      return;
    }

    // Allow all keys to be entered, we'll validate in onInput
    // Only prevent default for navigation and control keys
    if (this.isAllowedKey(event)) {
      return;
    }

    // For phone fields, allow all characters but we'll validate them in onInput
    // This allows the character to be entered and then validated
  }

  /**
   * Handles paste events for contact fields
   * @param event The clipboard event
   * @param contact The form group containing the contact data
   */
  public onPaste(event: ClipboardEvent, contact: UntypedFormGroup): void {
    if (
      contact.controls.type.value !== ContactType.MOBILE_PHONE &&
      contact.controls.type.value !== ContactType.PHONE
    ) {
      return;
    }

    event.preventDefault();
    const pastedText = event.clipboardData.getData('text');
    const cleanedText = pastedText.replace(this.MOBILE_PHONE_CLEAN_PATTERN, '');
    contact.controls.value.setValue(cleanedText);
  }

  /**
   * Checks if the pressed key is allowed
   * @param event The keyboard event
   * @returns boolean indicating if the key is allowed
   */
  private isAllowedKey(event: KeyboardEvent): boolean {
    const keyCode = event.keyCode;

    // Check for navigation and control keys
    const isNavigationKey = [
      this.ALLOWED_KEYS.BACKSPACE,
      this.ALLOWED_KEYS.TAB,
      this.ALLOWED_KEYS.ENTER,
      this.ALLOWED_KEYS.ESCAPE,
      this.ALLOWED_KEYS.DELETE,
      this.ALLOWED_KEYS.LEFT_ARROW,
      this.ALLOWED_KEYS.UP_ARROW,
      this.ALLOWED_KEYS.RIGHT_ARROW,
      this.ALLOWED_KEYS.DOWN_ARROW,
    ].includes(keyCode);

    // Check for Ctrl combinations
    const isCtrlCombination =
      event.ctrlKey &&
      [this.ALLOWED_KEYS.A, this.ALLOWED_KEYS.C, this.ALLOWED_KEYS.V, this.ALLOWED_KEYS.X].includes(
        keyCode,
      );

    // Check for home/end keys
    const isHomeEndKey = keyCode >= this.ALLOWED_KEYS.HOME && keyCode <= this.ALLOWED_KEYS.END;

    return isNavigationKey || isCtrlCombination || isHomeEndKey;
  }

  // Custom validator for phone numbers
  private phoneNumberValidator(control: UntypedFormControl): {[key: string]: any} | null {
    if (!control.value) {
      return null; // Let required validator handle empty values
    }

    const value = control.value.toString();
    if (!this.MOBILE_PHONE_PATTERN.test(value)) {
      return {invalidPhoneNumber: true};
    }

    return null;
  }

  /**
   * Handles input events for real-time validation
   * @param event The input event
   * @param contact The form group containing the contact data
   */
  public onInput(event: Event, contact: UntypedFormGroup): void {
    const contactType = contact.controls.type.value;
    const valueControl = contact.controls.value as UntypedFormControl;
    const contactId = contact.controls.id.value;
    const currentValue = valueControl.value;

    // Create the control key to check against original value
    const controlKey = `${contactId || 'new'}_${contactType}`;
    const originalValue = this.originalValues.get(controlKey);

    // Only validate if the value has actually changed from original
    if (originalValue !== currentValue) {
      // Mark as dirty and touched for immediate feedback
      valueControl.markAsDirty();
      valueControl.markAsTouched();

      if (contactType === ContactType.MOBILE_PHONE || contactType === ContactType.PHONE) {
        // Force validation update for phone numbers
        valueControl.updateValueAndValidity({emitEvent: true});

        // Trigger change detection to update the template
        setTimeout(() => {
          valueControl.updateValueAndValidity({emitEvent: true});
        }, 0);
      }
    }
  }

  // Custom validator that checks original value dynamically
  private dynamicPhoneNumberValidator(control: UntypedFormControl): {[key: string]: any} | null {
    // Get the contact group to find the ID and type
    const contactGroup = control.parent as UntypedFormGroup;
    if (!contactGroup) return null;

    const contactId = contactGroup.get('id')?.value;
    const contactType = contactGroup.get('type')?.value;
    const currentValue = control.value;

    // Get original value
    const controlKey = `${contactId || 'new'}_${contactType}`;
    const originalValue = this.originalValues.get(controlKey);

    // If this is a new contact (no original value), validate normally
    if (originalValue === undefined) {
      return this.phoneNumberValidator(control);
    }

    // If value hasn't changed from original, don't validate
    if (currentValue === originalValue) {
      return null;
    }

    // If value has changed, apply normal validation
    return this.phoneNumberValidator(control);
  }

  // Custom validator that checks original value dynamically for email
  private dynamicEmailValidator(control: UntypedFormControl): {[key: string]: any} | null {
    // Get the contact group to find the ID and type
    const contactGroup = control.parent as UntypedFormGroup;
    if (!contactGroup) return null;

    const contactId = contactGroup.get('id')?.value;
    const contactType = contactGroup.get('type')?.value;
    const currentValue = control.value;

    // Get original value
    const controlKey = `${contactId || 'new'}_${contactType}`;
    const originalValue = this.originalValues.get(controlKey);

    // If this is a new contact (no original value), validate normally
    if (originalValue === undefined) {
      return Validators.email(control);
    }

    // If value hasn't changed from original, don't validate
    if (currentValue === originalValue) {
      return null;
    }

    // If value has changed, apply normal validation
    return Validators.email(control);
  }

  // Get validators for a contact type
  getValidatorsForContactType(contactType: string): any[] {
    let validators = [Validators.required];

    if (contactType === ContactType.EMAIL) {
      validators.push(this.dynamicEmailValidator.bind(this));
    } else if (contactType === ContactType.MOBILE_PHONE || contactType === ContactType.PHONE) {
      validators.push(this.dynamicPhoneNumberValidator.bind(this));
    }

    return validators;
  }

  // Set up value change detection for a contact control
  setupValueChangeDetection(contact: UntypedFormGroup): void {
    const valueControl = contact.get('value') as UntypedFormControl;
    const contactId = contact.get('id')?.value;
    const contactType = contact.get('type')?.value;
    const controlKey = `${contactId || 'new'}_${contactType}`;

    valueControl.valueChanges.subscribe(newValue => {
      const originalValue = this.originalValues.get(controlKey);
      const hasChanged = originalValue !== newValue;

      // Only apply validation if the value has actually changed
      if (hasChanged) {
        valueControl.markAsDirty();
        valueControl.markAsTouched();
      } else {
        // If value is back to original, clear validation state
        valueControl.markAsPristine();
        valueControl.markAsUntouched();
      }

      // Force validation update
      valueControl.updateValueAndValidity({emitEvent: false});
    });
  }

  // Update contact type and validation
  updateContactType(contact: UntypedFormGroup, newType: string): void {
    const valueControl = contact.get('value') as UntypedFormControl;
    const idControl = contact.get('id') as UntypedFormControl;

    // Clear the value when type changes
    valueControl.setValue('');

    // Update the original value for the new type
    const controlKey = `${idControl.value || 'new'}_${newType}`;
    this.originalValues.set(controlKey, '');

    // Set validators based on new type
    const validators = this.getValidatorsForContactType(newType);
    valueControl.setValidators(validators);
    valueControl.markAsTouched();
    valueControl.updateValueAndValidity();
  }
} 